/**
 * SchedulingLinkFacade — query + mutate the weekly-availability
 * pattern (stored on `row.attributes`) and the per-slot cell
 * overrides (stored as `Slot` cells).
 *
 * Replaces both the deleted `SchedulingLinkRow` (cell-side API)
 * and the deleted `SchedulingLinkIndex` (attribute-side queries).
 * Attribute-driven facades don't need a rebuild — queries read
 * live from `row.attributes` each time. Keeps the surface area
 * small; the compute cost is negligible for a weekly pattern.
 *
 * Obtained via `row.as(SCHEDULING_LINK_FACADE)`. Available on any
 * obj multi-cell source row; queries return empty arrays when the
 * row has no scheduling-link attributes configured.
 *
 * The facade carries no writer. The SDK's own writes of the pattern and
 * the slot cells are `schedulingLinkWriterOf(facade)`
 * (`@tento-lona/sheets/internal-sdk`); an app edits a link through
 * `client.schedulingLinksFor(userId)`.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { sheetOf, sheetStores } from "../sheet/sheet-stores.ts";
import { NaiveDate } from "@tento-chrono";

import type { Slot } from "../core/dtype.ts";
import { FacadeKey, type FacadeFactory } from "./facade.ts";
import { SourceCells } from "../rows/variants/source-cells.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import { Actor } from "../tx/invalidation.ts";
import { sourceWriterOf } from "../rows/variants/source-row.ts";
import { CellId } from "../ids/ids.ts";
import { deepFrozen } from "../core/frozen-collections.ts";
import { Internals } from "../core/internals.ts";

/** `SchedulingLinkFacade`'s friend record (`Internals`): its constructor registers each instance. */
type SchedulingLinkFacadeInternals = {
  readonly writer: (facade: SchedulingLinkFacade) => SchedulingLinkFacade.Writer;
};
const schedulingLinkFacadeInternals = new Internals<SchedulingLinkFacade, SchedulingLinkFacadeInternals>("SchedulingLinkFacade");

/**
 * **The writers of a scheduling-link facade** — the weekly pattern
 * (`setAvailabilityLocal`, `addAvailabilityLocal`, `removeAvailabilityLocal`:
 * the row's attributes, locally) and the slot cells (`putSlot`, persisted;
 * `removeSlotLocal`, local only). The SDK-private door: the facade a read
 * hands out (`row.as(SCHEDULING_LINK_FACADE)`) carries no writer. On
 * `@tento-lona/sheets/internal-sdk` and `@tento-lona/sheets/internal-testing`,
 * not on a barrel.
 */
export function schedulingLinkWriterOf(facade: SchedulingLinkFacade): SchedulingLinkFacade.Writer {
  return schedulingLinkFacadeInternals.of(facade).writer(facade);
}

/** The facade key that reaches a source row's scheduling-link facade. */
export const SCHEDULING_LINK_FACADE = new FacadeKey<SchedulingLinkFacade>(
  "scheduling-link",
);

/** A scheduling link row's attributes: its slug, description and owner name. */
export interface SchedulingLinkAttributes {
  slug: string;
  description?: string;
  /** Display name of the link owner, shown on the public booking page. */
  ownerName?: string;
  durationMinutes: number;
  timezone: string;
  availability: SchedulingLinkAttributes.WeeklyAvailability[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * How this row renders in the calendar.
   * - "cells": editable per-slot overrides (default).
   * - "spans": read-only baseline weekly pattern.
   */
  renderMode?: SchedulingLinkAttributes.RenderMode;
}

/**
 * Types and values that go with `SchedulingLinkAttributes`:
 * `WeeklyAvailability`, `RenderMode`, `Decoded`, `decode`,
 * `availabilityToJson`.
 */
export namespace SchedulingLinkAttributes {
  export interface WeeklyAvailability {
    dayOfWeek: number;
    startMinutes: number;
    endMinutes: number;
    isAvailable: boolean;
  }

  export type RenderMode = "cells" | "spans";

  /**
   * A row's attribute bag as `decode` reads it: the `slug` it requires, and
   * every other field as stored — `null` when the bag does not carry it (the
   * bag is unvalidated row JSON, so a reader defaults what it needs).
   */
  export type Decoded = {
    readonly slug: string;
    readonly description: Option<string>;
    readonly ownerName: Option<string>;
    readonly durationMinutes: Option<number>;
    readonly timezone: Option<string>;
    readonly availability: Option<WeeklyAvailability[]>;
    readonly isActive: Option<boolean>;
    readonly createdAt: Option<string>;
    readonly updatedAt: Option<string>;
    readonly renderMode: Option<RenderMode>;
  };

  /**
   * Decode a row's attribute bag as scheduling-link configuration: `null`
   * unless it carries a `slug`. Every other field is optional (the bag is
   * row JSON); malformed availability entries are dropped.
   */
  export function decode(
    attributes: JsonObject,
  ): Option<SchedulingLinkAttributes.Decoded> {
    const slug = Json.string(attributes, "slug");
    if (slug == null || slug.length === 0) return null;
    const availability = Json.array(attributes, "availability");
    return {
      slug,
      description: Json.string(attributes, "description"),
      ownerName: Json.string(attributes, "ownerName"),
      durationMinutes: Json.number(attributes, "durationMinutes"),
      timezone: Json.string(attributes, "timezone"),
      availability: availability == null ? null : decodeAvailability(availability),
      isActive: Json.boolean(attributes, "isActive"),
      createdAt: Json.string(attributes, "createdAt"),
      updatedAt: Json.string(attributes, "updatedAt"),
      renderMode: Json.oneOf(attributes, "renderMode", RENDER_MODES),
    };
  }

  const RENDER_MODES: readonly RenderMode[] = ["cells", "spans"];

  function decodeAvailability(list: readonly Json[]): WeeklyAvailability[] {
    const out: WeeklyAvailability[] = [];
    for (const item of list) {
      const obj = Json.object(item);
      const dayOfWeek = Json.number(obj, "dayOfWeek");
      const startMinutes = Json.number(obj, "startMinutes");
      const endMinutes = Json.number(obj, "endMinutes");
      const isAvailable = Json.boolean(obj, "isAvailable");
      if (
        dayOfWeek == null || startMinutes == null || endMinutes == null ||
        isAvailable == null
      ) continue;
      out.push({ dayOfWeek, startMinutes, endMinutes, isAvailable });
    }
    return out;
  }

  /** The JSON form of an availability list, as the attribute bag stores it. */
  export function availabilityToJson(
    entries: readonly WeeklyAvailability[],
  ): Json[] {
    return entries.map((e) => ({
      dayOfWeek: e.dayOfWeek,
      startMinutes: e.startMinutes,
      endMinutes: e.endMinutes,
      isAvailable: e.isAvailable,
    }));
  }
}

export class SchedulingLinkFacade {
  static readonly #internals: SchedulingLinkFacadeInternals = {
    writer: (facade) => facade.#writerFace(),
  };

  readonly #row: SourceRow;

  constructor(row: SourceRow) {
    schedulingLinkFacadeInternals.register(this, SchedulingLinkFacade.#internals);
    this.#row = row;
  }

  /** The writer face (`schedulingLinkWriterOf`), built on first use. */
  #writer: Option<SchedulingLinkFacade.Writer> = null;

  #writerFace(): SchedulingLinkFacade.Writer {
    this.#writer ??= Object.freeze({
      setAvailabilityLocal: (entries: readonly SchedulingLinkAttributes.WeeklyAvailability[]) =>
        this.#setAvailabilityLocal(entries),
      addAvailabilityLocal: (entry: SchedulingLinkAttributes.WeeklyAvailability) =>
        this.#setAvailabilityLocal([...this.allAvailability(), entry]),
      removeAvailabilityLocal: (dayOfWeek: number, startMinutes: number) =>
        this.#setAvailabilityLocal(
          this.allAvailability().filter(
            (e) => !(e.dayOfWeek === dayOfWeek && e.startMinutes === startMinutes),
          ),
        ),
      putSlot: (date: NaiveDate.Partial, slot: Slot) =>
        sourceWriterOf(this.#row).write<Slot>(date, slot, CellId.of(slot.id), Actor.EXTERNAL).then(() => {}),
      removeSlotLocal: (date: NaiveDate.Partial, slotId: string) =>
        sourceWriterOf(this.#row).local.clearCell(date, CellId.of(slotId), Actor.EXTERNAL),
    });
    return this.#writer;
  }

  // ── Attribute-driven queries (weekly availability) ────────────────

  /**
   * The row's `SchedulingLinkAttributes` bag; null if the row isn't
   * configured (no `slug`). The bag is unvalidated row JSON, so every
   * field is `Option` — readers default what they need.
   */
  config(): Option<SchedulingLinkAttributes.Decoded> {
    return this.#row.attributesAs(SchedulingLinkAttributes.decode);
  }

  /** Available weekly slots across all days (merged + filtered). */
  allAvailability(): readonly SchedulingLinkAttributes.WeeklyAvailability[] {
    const config = this.config();
    if (!config) return EMPTY;
    return Object.freeze((config.availability ?? EMPTY).filter((a) => a.isAvailable));
  }

  /** Available weekly slots on a single day-of-week (0 = Sunday … 6 = Saturday). */
  availabilityForDay(
    dow: number,
  ): readonly SchedulingLinkAttributes.WeeklyAvailability[] {
    return this.allAvailability().filter((a) => a.dayOfWeek === dow);
  }

  // ── Attribute writer (`schedulingLinkWriterOf`; the row metadata store) ──

  /** `SchedulingLinkFacade.Writer.setAvailabilityLocal`. */
  #setAvailabilityLocal(
    entries: readonly SchedulingLinkAttributes.WeeklyAvailability[],
  ): void {
    const config = this.config();
    if (!config) {
      throw new Error(
        `SchedulingLinkFacade.setAvailabilityLocal: row ${this.#row.id} has no scheduling-link attributes`,
      );
    }
    const merged = mergeAvailability([...entries]);
    sheetStores(sheetOf(this.#row)).rowMetadata.patchAttributes(this.#row.id, {
      availability: SchedulingLinkAttributes.availabilityToJson(merged),
    });
  }

  // ── Cell-side queries (slot overrides) ────────────────────────────

  slotsOn(date: NaiveDate.Partial): readonly Slot[] {
    const out: Slot[] = [];
    for (const cell of SourceCells.dates(this.#row.peekCells(date.toRange()))) {
      if (isSlot(cell.value)) out.push(cell.value);
    }
    return out;
  }
}

/** Types and values that go with `SchedulingLinkFacade`: `Writer`, `factory`. */
export namespace SchedulingLinkFacade {
  /** The writers `schedulingLinkWriterOf(facade)` hands the SDK (see there). */
  export interface Writer {
    /**
     * Replace the row's weekly availability (overlapping entries merged), in
     * its attributes, locally; nothing is sent. Throws when the row carries
     * no scheduling-link attributes.
     */
    setAvailabilityLocal(entries: readonly SchedulingLinkAttributes.WeeklyAvailability[]): void;
    /** Add one weekly entry, as `setAvailabilityLocal` does. */
    addAvailabilityLocal(entry: SchedulingLinkAttributes.WeeklyAvailability): void;
    /** Drop the weekly entry at `(dayOfWeek, startMinutes)`, as `setAvailabilityLocal` does. */
    removeAvailabilityLocal(dayOfWeek: number, startMinutes: number): void;
    /** Upsert the slot cell `slot.id` on `date`, persisted; rejects on a refusal. */
    putSlot(date: NaiveDate.Partial, slot: Slot): Promise<void>;
    /** Clear the slot cell `slotId` on `date` locally; nothing is sent. */
    removeSlotLocal(date: NaiveDate.Partial, slotId: string): void;
  }

  export const factory: FacadeFactory<SchedulingLinkFacade> = deepFrozen({
    key: SCHEDULING_LINK_FACADE,
    build(row: SourceRow): Option<SchedulingLinkFacade> {
      if (row.descriptor.role !== "source") return null;
      if (row.descriptor.dtype !== "obj") return null;
      if (row.descriptor.cellCardinality !== "multi") return null;
      return new SchedulingLinkFacade(row);
    },
  });
}

/**
 * Merge overlapping / adjacent availability entries for the same
 * day. Entries on different days are never merged with each other.
 * Ported from lona-js `scheduling-link-index.ts:mergeAvailability`.
 */
export function mergeAvailability(
  entries: readonly SchedulingLinkAttributes.WeeklyAvailability[],
): SchedulingLinkAttributes.WeeklyAvailability[] {
  const byDay = new Map<
    number,
    SchedulingLinkAttributes.WeeklyAvailability[]
  >();
  for (const entry of entries) {
    if (!entry.isAvailable) continue;
    let group = byDay.get(entry.dayOfWeek);
    if (!group) {
      group = [];
      byDay.set(entry.dayOfWeek, group);
    }
    group.push(entry);
  }

  const result: SchedulingLinkAttributes.WeeklyAvailability[] = [];
  for (const [, group] of byDay) {
    group.sort((a, b) => a.startMinutes - b.startMinutes);
    const [head, ...rest] = group;
    if (head === undefined) continue;
    let current = { ...head };
    for (const next of rest) {
      if (next.startMinutes <= current.endMinutes) {
        current.endMinutes = Math.max(current.endMinutes, next.endMinutes);
      } else {
        result.push(current);
        current = { ...next };
      }
    }
    result.push(current);
  }
  return result;
}

const EMPTY: readonly SchedulingLinkAttributes.WeeklyAvailability[] = Object.freeze([]);

function isSlot(value: unknown): value is Slot {
  if (typeof value !== "object" || value === null) return false;
  if (!("id" in value) || typeof value.id !== "string") return false;
  return "start" in value && isInstant(value.start) && "end" in value && isInstant(value.end);
}

/** A DateTime-ish value: an object with a numeric `mse` (ms since epoch). */
function isInstant(value: unknown): boolean {
  return typeof value === "object" && value !== null && "mse" in value &&
    typeof value.mse === "number";
}
